import React, { useState } from 'react'
import {
  Box,
  Button,
  Card,
  TextField,
  MenuItem,
  Typography,
  Stack,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
} from '@mui/material'
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Check as ApproveIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import {
  useGetStockAdjustmentsQuery,
  useCreateStockAdjustmentMutation,
  useApproveStockAdjustmentMutation,
  useGetShrinkageReportQuery,
  useExportStockAdjustmentsMutation,
} from '../store/services/inventoryApi'
import { formatCompactCurrency } from '../utils/currency'

const StockAdjustmentsEnhanced: React.FC = () => {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)

  
  // Filter states
  const [filters, setFilters] = useState({
    adjustment_type: 'all',
    status: 'all',
    start_date: '',
    end_date: '',
  })
  
  // Dialog states
  const [, setIsCreateOpen] = useState(false)
  const [_selectedAdjustment, setSelectedAdjustment] = useState<any>(null)
  const [, setIsViewOpen] = useState(false)
  const [, setIsShrinkageReportOpen] = useState(false)
  
  const { enqueueSnackbar } = useSnackbar()

  // RTK Query hooks
  const { 
    data: adjustmentsData, 
    isLoading, 
    error,
    refetch 
  } = useGetStockAdjustmentsQuery({
    page: page + 1,
    per_page: pageSize,
    adjustment_type: filters.adjustment_type !== 'all' ? filters.adjustment_type : undefined,
    status: filters.status !== 'all' ? filters.status : undefined,
    start_date: filters.start_date || undefined,
    end_date: filters.end_date || undefined,
  })

  const { data: shrinkageData } = useGetShrinkageReportQuery({
    start_date: filters.start_date || undefined,
    end_date: filters.end_date || undefined,
  })

  const [] = useCreateStockAdjustmentMutation()
  const [approveAdjustment] = useApproveStockAdjustmentMutation()
  const [exportAdjustments] = useExportStockAdjustmentsMutation()

  const adjustments = adjustmentsData?.adjustments || []
  const totalRows = adjustmentsData?.total_count || 0

  // Debug logging
  React.useEffect(() => {
    if (adjustmentsData) {
      console.log('Stock Adjustments Data:', adjustmentsData)
      console.log('Adjustments Array:', adjustments)
      console.log('Total Rows:', totalRows)
    }
    if (error) {
      console.error('Stock Adjustments Error:', error)
    }
  }, [adjustmentsData, error])

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setPage(0) // Reset to first page when filters change
  }

  const handleApprove = async (adjustmentId: string) => {
    try {
      await approveAdjustment(adjustmentId).unwrap()
      enqueueSnackbar('Stock adjustment approved successfully', { variant: 'success' })
      refetch()
    } catch (error) {
      enqueueSnackbar('Failed to approve adjustment', { variant: 'error' })
    }
  }

  const handleExport = async (format: 'excel' | 'csv' = 'excel') => {
    try {
      const blob = await exportAdjustments({
        ...filters,
        format,
      }).unwrap()
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `stock_adjustments_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      enqueueSnackbar('Stock adjustments exported successfully', { variant: 'success' })
    } catch (error) {
      enqueueSnackbar('Failed to export adjustments', { variant: 'error' })
    }
  }

  const getAdjustmentTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'damage': 'error',
      'loss': 'error',
      'theft': 'error',
      'found': 'success',
      'correction': 'info',
      'manual': 'primary',
      'return': 'success',
    }
    return colors[type] || 'default'
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending': 'warning',
      'approved': 'success',
      'rejected': 'error',
    }
    return colors[status] || 'default'
  }

  const pendingCount = adjustments.filter((adj: any) => adj.status === 'pending').length

  return (
    <Box sx={{ p: 2 }}>
      {/* Compact toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        {/* Inline summary chips */}
        {shrinkageData && (
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={`Shrinkage: ${formatCompactCurrency(shrinkageData.total_shrinkage_value || 0)}`} color="error" variant="outlined" />
            <Chip size="small" label={`Rate: ${shrinkageData.shrinkage_percentage?.toFixed(1) || 0}%`} color="warning" variant="outlined" />
            <Chip size="small" label={`Total: ${totalRows}`} variant="outlined" />
            {pendingCount > 0 && <Chip size="small" label={`Pending: ${pendingCount}`} color="warning" />}
          </Stack>
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setIsCreateOpen(true)} sx={{ textTransform: 'none' }}>New Adjustment</Button>
        <Button size="small" variant="outlined" startIcon={<ReportIcon />} onClick={() => setIsShrinkageReportOpen(true)} sx={{ textTransform: 'none' }}>Shrinkage</Button>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExport('excel')} sx={{ textTransform: 'none' }}>Export</Button>
      </Box>

      {/* Compact filter bar */}
      <Paper variant="outlined" sx={{ px: 1.5, py: 1, mb: 1.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select value={filters.adjustment_type} label="Type" onChange={(e) => handleFilterChange('adjustment_type', e.target.value)}>
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="damage">Damage</MenuItem>
              <MenuItem value="loss">Loss</MenuItem>
              <MenuItem value="theft">Theft</MenuItem>
              <MenuItem value="found">Found</MenuItem>
              <MenuItem value="correction">Correction</MenuItem>
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="return">Return</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filters.status} label="Status" onChange={(e) => handleFilterChange('status', e.target.value)}>
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="From" type="date" value={filters.start_date} onChange={(e) => handleFilterChange('start_date', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 148 }} />
          <TextField size="small" label="To" type="date" value={filters.end_date} onChange={(e) => handleFilterChange('end_date', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 148 }} />
        </Stack>
      </Paper>

      {isLoading && <LinearProgress sx={{ mb: 1 }} />}

      {/* Dense table */}
      <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Type</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Cost Impact</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>By</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Date</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adjustments.length === 0 && !isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No adjustments found</TableCell>
                </TableRow>
              ) : adjustments.map((adjustment: any) => (
                <TableRow key={adjustment.id} hover>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{adjustment.product_name || 'N/A'}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{adjustment.sku || ''} {adjustment.variant_name || ''}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={adjustment.adjustment_type || '—'} size="small" color={getAdjustmentTypeColor(adjustment.adjustment_type) as any} variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600} color={adjustment.quantity > 0 ? 'success.main' : 'error.main'}>
                      {adjustment.quantity > 0 ? '+' : ''}{adjustment.quantity || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={(adjustment.total_cost_impact || 0) >= 0 ? 'success.main' : 'error.main'}>
                      {formatCompactCurrency(adjustment.total_cost_impact || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    <Typography variant="body2" noWrap>{adjustment.reason || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={adjustment.status || '—'} size="small" color={getStatusColor(adjustment.status) as any} sx={{ fontSize: '0.7rem', height: 20 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>{adjustment.created_by_admin_name || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {adjustment.created_at ? new Date(adjustment.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => { setSelectedAdjustment(adjustment); setIsViewOpen(true) }}>
                          <ViewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      {adjustment.status === 'pending' && (
                        <Tooltip title="Approve">
                          <IconButton size="small" color="success" onClick={() => handleApprove(adjustment.id)}>
                            <ApproveIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={totalRows}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0) }}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      </Card>

      {error && <Alert severity="error" sx={{ mt: 1.5 }}>Failed to load adjustments. Please try again.</Alert>}
    </Box>
  )
}

export default StockAdjustmentsEnhanced